import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { Avatar, Card, EmptyState, Gift01 } from "@opendate-app/setlist";
import useGiftCardPurchase, { ARCHIVED, AVAILABLE, SOLD_OUT } from "../useGiftCardPurchase";
import GiftCardDetails from "./GiftCardDetails";
import GiftCardPurchaseForm from "./GiftCardPurchaseForm";
import GiftCardConfirmation from "./GiftCardConfirmation";
import { deliveryShape, productShape, quoteShape, teamShape } from "../giftCardPurchasePropTypes";
import "../styles/giftCardPurchase.css";

const MINIMUM_ELEMENTS_AMOUNT_CENTS = 100;

const UNAVAILABLE_COPY = {
  [SOLD_OUT]: { title: "Sold out", description: "This gift card has sold out. Check back later." },
  [ARCHIVED]: { title: "No longer for sale", description: "This gift card isn't sold anymore." },
};
const NOT_FOR_SALE_COPY = { title: "Not for sale right now", description: "This gift card can't be bought at the moment." };

const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

const GiftCardPurchase = ({
  availability,
  product,
  team,
  currency,
  initialQuote,
  quoteUrl,
  ordersUrl,
  upcomingShowsUrl,
  messageMaxLength,
  delivery,
  stripePublishableKey,
  stripeConnectedAccountId,
  turnstileSiteKey,
}) => {
  const purchase = useGiftCardPurchase({ product, availability, initialQuote, quoteUrl, ordersUrl, delivery });
  const lastTotalCents = useRef(initialQuote ? initialQuote.total_cents : MINIMUM_ELEMENTS_AMOUNT_CENTS);
  if (purchase.quote) lastTotalCents.current = purchase.quote.total_cents;

  const stripePromise = useMemo(
    () =>
      loadStripe(stripePublishableKey, stripeConnectedAccountId ? { stripeAccount: stripeConnectedAccountId } : undefined),
    [stripePublishableKey, stripeConnectedAccountId]
  );

  const unavailable = UNAVAILABLE_COPY[purchase.availability] || NOT_FOR_SALE_COPY;

  return (
    <div className="gift-card-purchase">
      <header className="gift-card-purchase-header">
        <Avatar size="md" src={team.logoUrl || undefined} initials={initials(team.name)} alt={team.name} />
        <div>
          <div className="gift-card-purchase-header-name">{team.name}</div>
          {team.location && <div className="gift-card-purchase-header-location">{team.location}</div>}
        </div>
      </header>

      {purchase.confirmation ? (
        <GiftCardConfirmation
          confirmation={purchase.confirmation}
          product={product}
          team={team}
          currency={currency}
          upcomingShowsUrl={upcomingShowsUrl}
          onBuyAnother={purchase.buyAnother}
        />
      ) : (
        <div className="gift-card-purchase-layout">
          <GiftCardDetails product={product} team={team} currency={currency} amountCents={purchase.amountCents} />
          {purchase.availability === AVAILABLE ? (
            <Elements
              stripe={stripePromise}
              options={{
                mode: "payment",
                amount: lastTotalCents.current,
                currency: currency.toLowerCase(),
                paymentMethodTypes: ["card"],
              }}
            >
              <GiftCardPurchaseForm
                product={product}
                team={team}
                currency={currency}
                messageMaxLength={messageMaxLength}
                delivery={delivery}
                turnstileSiteKey={turnstileSiteKey}
                purchase={purchase}
              />
            </Elements>
          ) : (
            <Card>
              <div data-testid="gift-card-unavailable">
                <EmptyState icon={Gift01} title={unavailable.title} description={unavailable.description} />
              </div>
            </Card>
          )}
        </div>
      )}

      <p className="gift-card-purchase-powered">Powered by Opendate</p>
    </div>
  );
};

GiftCardPurchase.propTypes = {
  availability: PropTypes.string.isRequired,
  product: productShape.isRequired,
  team: teamShape.isRequired,
  currency: PropTypes.string.isRequired,
  initialQuote: quoteShape,
  quoteUrl: PropTypes.string.isRequired,
  ordersUrl: PropTypes.string.isRequired,
  upcomingShowsUrl: PropTypes.string.isRequired,
  messageMaxLength: PropTypes.number.isRequired,
  delivery: deliveryShape.isRequired,
  stripePublishableKey: PropTypes.string.isRequired,
  stripeConnectedAccountId: PropTypes.string,
  turnstileSiteKey: PropTypes.string,
};

export default GiftCardPurchase;
